import React, { FC } from 'react'

import clsx from 'clsx'
import {
  Control,
  Controller,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form'
import { UseFormSetValue, UseFormTrigger } from 'react-hook-form/dist/types/form'
import Select from 'react-select'

import MessageHelperComp from '../../../../../helpers/MessageHelper'
import CustomDatePicker from '../../../../common/inputs/CustomDatePicker'
import Input from '../../../../common/inputs/Input'
import IncrementTimeSelector from './IncrementTimeSelector'
import { IconCheck } from '../../../../icons/IconCheck'

import { getReactPickerDate } from '../../../../../models/interface.helper'
import { cn } from '../../../../../lib/utils'

import { MODAL_CONSTS } from '../../../../../const/modal-const'
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'

interface ScheduleItemByRiderProps {
  index: number
  hasChildren?: boolean
  handleModal: (showHide: boolean, typeOfModal: string, dataToPassOn?: any) => void
  register: UseFormRegister<any>
  trigger: UseFormTrigger<any>
  control: Control<any>
  watch: UseFormWatch<any>
  setValue: UseFormSetValue<any>
  remove?: UseFieldArrayRemove
  areAllFieldsValid: (index: number) => boolean
  locationOptions: { label: string; value: string }[]
  parentSelector?: `registrationFees.${number}`
  selector:
    | `custom.${number}`
    | `registrationFees.${number}`
    | `registrationFees.${number}.children.${number}`
  errorSelector: { [key: string]: { message: string } }
  selectedIds: string[]
  onSelected: (id: string) => void
  isBatchSchedule: boolean
  registrationByDay: IRegistrationByDayInterface[]
}

export const ScheduleItemByRider: FC<ScheduleItemByRiderProps> = ({
  index,
  handleModal,
  register,
  trigger,
  control,
  watch,
  setValue,
  areAllFieldsValid,
  locationOptions,
  remove,
  selector,
  parentSelector,
  hasChildren,
  errorSelector,
  selectedIds,
  onSelected,
  isBatchSchedule,
  registrationByDay,
}) => {
  const openAddNotesModal = (noteInputRef: any) => {
    handleModal(true, MODAL_CONSTS.ADD_NOTE, {
      noteInputRef,
      setValue: setValue,
      trigger,
    })
  }

  const countRiders = registrationByDay.filter(
    (rider) => rider.uuid === watch(`${selector}.uuid`)
  ).length

  return (
    <div
      className={clsx(
        'min-h-[56px] mt-4 w-full relative border border-SeabiscuitLightThemeColorD3 rounded-lg px-4 py-2 flex flex-wrap items-center justify-between gap-y-2',
        areAllFieldsValid(index)
          ? 'bg-SeabiscuitGrayThemeColor'
          : 'bg-white border-SeabiscuitLightThemeColorD3'
      )}
    >
      {/* Name Field - Takes most space */}
      <div className="relative w-full sm:w-[48%] lg:w-[25%]">
        <Input
          placeholder="Name custom time"
          register={register}
          name={`${selector}.name`}
          value={watch(`${selector}.name`)}
          className="p-0 border-none bg-transparent w-full"
        />
        {!!errorSelector?.name?.message ? (
          <MessageHelperComp
            className="absolute z-10 bottom-0 left-0 translate-y-full"
            isError={true}
            message={errorSelector?.name?.message}
          />
        ) : null}
      </div>

      {!hasChildren && (
        <>
          {/* Location Field */}
          <div className="relative w-full sm:w-[48%] lg:w-[15%]">
            <Controller
              control={control}
              name={`${selector}.location`}
              render={() => {
                return (
                  <Select
                    isDisabled={locationOptions?.[0]?.label?.length <= 0}
                    className="w-full"
                    classNamePrefix="entries-select"
                    isClearable={false}
                    isSearchable={true}
                    placeholder="Facility"
                    value={
                      watch(`${selector}.location`)
                        ? {
                            label: watch(`${selector}.location`),
                            value: watch(`${selector}.location`),
                          }
                        : null
                    }
                    options={locationOptions}
                    onChange={(newValue) => {
                      if (newValue) {
                        setValue(`${selector}.location`, newValue.value)
                        if (parentSelector && index === 0)
                          setValue(`${parentSelector}.location`, newValue.value)
                      }
                    }}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        minHeight: '40px',
                      }),
                    }}
                  />
                )
              }}
            />
            {!!errorSelector?.location?.message ? (
              <MessageHelperComp
                className="absolute z-10 bottom-0 left-0 translate-y-full"
                isError={true}
                message={errorSelector?.location?.message}
              />
            ) : null}
          </div>

          {/* Start Date Field */}
          <div className="relative w-full sm:w-[48%] lg:w-[13%]">
            <Controller
              name={`${selector}.startDate`}
              control={control}
              render={({ field: { onChange, onBlur } }) => {
                return (
                  <CustomDatePicker
                    onChange={(date) => {
                      onChange(date)
                      if (parentSelector && index === 0)
                        setValue(`${parentSelector}.startDate`, date)
                    }}
                    onBlur={onBlur}
                    targetId="fieldContainer"
                    selected={getReactPickerDate(watch(`${selector}.startDate`))}
                    peekNextMonth
                    dropdownMode="select"
                    showMonthDropdown
                    minDate={new Date()}
                    showYearDropdown
                    placeholderText="Day"
                    className="p-0 border-none bg-transparent w-full"
                    dateFormat="EEEE"
                    showArrow
                  />
                )
              }}
            />
            {!!errorSelector?.startDate?.message ? (
              <MessageHelperComp
                className="absolute z-10 bottom-0 left-0 translate-y-full"
                isError={true}
                message={errorSelector?.startDate?.message}
              />
            ) : null}
          </div>

          {/* Increment Selector - Needs more space for the time picker */}
          <div className="relative w-full sm:w-[48%] lg:w-[12%] min-w-[130px]">
            <IncrementTimeSelector
              initialTime={watch(`${selector}.increment`)}
              saveHandler={(time) => {
                setValue(`${selector}.increment`, time)
                if (parentSelector && index === 0) setValue(`${parentSelector}.increment`, time)
                trigger(`${selector}.increment`)
              }}
              placeholder="Set Increment"
            />
            {!!errorSelector?.increment?.message ? (
              <MessageHelperComp
                className="absolute z-10 bottom-0 left-0 translate-y-full"
                isError={true}
                message={errorSelector?.increment?.message}
              />
            ) : null}
          </div>

          {/* Note Field */}
          <div className="relative w-full sm:w-[48%] lg:w-[15%]">
            <Input
              readOnly
              placeholder="Enter note"
              register={register}
              name={`${selector}.note`}
              onClick={(e) => openAddNotesModal(e.target)}
              value={watch(`${selector}.note`)}
              className="p-0 underline bg-transparent border-none text-[14px] text-SeabiscuitDark200ThemeColor w-full rounded-md flex items-center cursor-pointer hover:no-underline overflow-hidden truncate"
            />
          </div>

          {/* Entries Field */}
          <div
            className={cn(
              'relative w-full sm:w-[48%] lg:w-[10%] text-[14px]',
              !countRiders && 'opacity-50'
            )}
          >
            {countRiders} entries
          </div>

          {/* Checkbox Field */}
          {isBatchSchedule && (
            <button
              type="button"
              className={cn(
                'border border-solid w-[20px] h-[20px] text-[#00B6AA] rounded-full flex items-center justify-center',
                selectedIds.includes(watch(`${selector}.uuid`))
                  ? 'border-[#00B6AA]'
                  : 'border-[#D3DAEE]'
              )}
              onClick={() => onSelected(watch(`${selector}.uuid`))}
            >
              {selectedIds.includes(watch(`${selector}.uuid`)) && <IconCheck />}
            </button>
          )}

          {/* Remove Button - Positioned absolutely */}
          {remove && (
            <button
              className="absolute top-4 -right-1 translate-x-full flex items-center w-6 cursor-pointer z-20"
              onClick={() => {
                handleModal(true, MODAL_CONSTS.REMOVE_CONFIRMATION, {
                  removeItem: function () {
                    remove(index)
                  },
                })
              }}
            >
              <img src="/assets/og_icons/Cancel.svg" className="w-full" alt="remove" />
            </button>
          )}
        </>
      )}
    </div>
  )
}
